import { useMemo } from 'react';
import type { GeoPoint } from '@/lib/geo/geoPoint';
import { GeoProjection, paddedBox, scaledBox, type Point2D } from '@/lib/geo/geoProjection';
import { shapesBoundingBox, type HoleShapes } from '@/lib/geo/holeGeometry';
import { teeColor } from '@/routes/record/teeColor';
import styles from './SchematicHole.module.css';

/**
 * The hole drawn from its own GeoJSON, in the terrain colours inherited from the watch
 * (canvas 1a, "Terreno · herdado do relógio").
 *
 * This is the fallback when satellite tiles are unavailable — and the canvas is explicit
 * that the fall back is silent: "o esquemático é o fallback silencioso, sem estado de erro".
 * It is also the only mode that works with no network at all.
 *
 * Drawing order matters and is taken from the watch's canvas: tree areas sit under
 * everything, then rough, fairway, water, out-of-bounds, bunkers, green, tees, tree crowns,
 * and finally the pin. Getting this wrong hides a bunker under a fairway.
 */

export interface SchematicHoleProps {
  shapes: HoleShapes;
  /** The tee to orient from and draw the line to the pin from. */
  teeName?: string | null;
  width?: number;
  height?: number;
  /** 1 = fit the hole; below 1 zooms in. */
  zoom?: number;
  /** Accessible description — the drawing itself carries no text. */
  label: string;
}

const VIEW_W = 660;
const VIEW_H = 1050;

export function SchematicHole({
  shapes,
  teeName,
  width = VIEW_W,
  height = VIEW_H,
  zoom = 1,
  label,
}: SchematicHoleProps) {
  const model = useMemo(() => {
    const tee = shapes.tees.find((t) => t.name === teeName)?.point ?? shapes.tees[0]?.point ?? null;

    // Pad so nothing touches the edge, then let zoom shrink the box (the watch's approach:
    // zooming in means framing less world, not scaling the drawing).
    const box = scaledBox(paddedBox(shapesBoundingBox(shapes), 0.08), zoom);

    const projection = GeoProjection.build(
      box,
      { width, height },
      // Orienting tee→pin puts the shot up the screen, which is how a player reads a hole.
      tee ? { orientFromTo: { from: tee, to: shapes.pin }, padding: 16 } : { padding: 16 },
    );

    const path = (ring: GeoPoint[]) =>
      ring
        .map((p) => projection.project(p))
        .map(pointToPair)
        .join(' ');

    return {
      projection,
      tee,
      teeAt: tee ? projection.project(tee) : null,
      pinAt: projection.project(shapes.pin),
      greenPath: shapes.green.length >= 3 ? path(shapes.green) : null,
      fairwayPath: shapes.fairway.length >= 3 ? path(shapes.fairway) : null,
      roughPaths: shapes.rough.map(path),
      waterPaths: shapes.water.map(path),
      obPaths: shapes.outOfBounds.map(path),
      bunkerPaths: shapes.bunkers.map(path),
      treeAreaPaths: shapes.treeAreas.map(path),
      trees: shapes.trees.map((t) => projection.project(t)),
      tees: shapes.tees.map((t) => ({ ...t, at: projection.project(t.point) })),
    };
  }, [shapes, teeName, width, height, zoom]);

  const treeRadius = Math.max(4, Math.min(width, height) * 0.028);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={styles.svg}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Under everything: the footprint of wooded areas. */}
      {model.treeAreaPaths.map((d, i) => (
        <polygon key={`ta${i}`} points={d} className={styles.treeArea} />
      ))}

      {model.roughPaths.map((d, i) => (
        <polygon key={`r${i}`} points={d} className={styles.rough} />
      ))}

      {model.fairwayPath && <polygon points={model.fairwayPath} className={styles.fairway} />}

      {model.waterPaths.map((d, i) => (
        <polygon key={`w${i}`} points={d} className={styles.water} />
      ))}

      {/* Out of bounds is a boundary, so it is stroked rather than filled. */}
      {model.obPaths.map((d, i) => (
        <polygon key={`ob${i}`} points={d} className={styles.ob} />
      ))}

      {model.bunkerPaths.map((d, i) => (
        <polygon key={`b${i}`} points={d} className={styles.bunker} />
      ))}

      {model.greenPath && <polygon points={model.greenPath} className={styles.green} />}

      {model.tees.map((t, i) => (
        <rect
          key={`t${i}`}
          x={t.at.x - 14}
          y={t.at.y - 6}
          width={28}
          height={12}
          rx={4}
          fill={teeColor({ name: t.name ?? '', color: '' })}
          className={styles.teeMark}
        />
      ))}

      {/* Crowns last among the terrain, each with a shadow offset down-right. */}
      {model.trees.map((t, i) => (
        <g key={`tr${i}`}>
          <circle cx={t.x + 3} cy={t.y + 3} r={treeRadius} className={styles.treeShadow} />
          <circle cx={t.x} cy={t.y} r={treeRadius} className={styles.tree} />
        </g>
      ))}

      {/* The line of play, and the one gold mark on this drawing. */}
      {model.teeAt && (
        <>
          <line
            x1={model.teeAt.x}
            y1={model.teeAt.y}
            x2={model.pinAt.x}
            y2={model.pinAt.y}
            className={styles.playLine}
          />
          <circle cx={model.teeAt.x} cy={model.teeAt.y} r={7} className={styles.teeDot} />
        </>
      )}

      <line
        x1={model.pinAt.x}
        y1={model.pinAt.y}
        x2={model.pinAt.x}
        y2={model.pinAt.y - 44}
        className={styles.flagPole}
      />
      <polygon
        points={`${model.pinAt.x},${model.pinAt.y - 44} ${model.pinAt.x + 28},${model.pinAt.y - 34} ${model.pinAt.x},${model.pinAt.y - 24}`}
        className={styles.flag}
      />
      <circle cx={model.pinAt.x} cy={model.pinAt.y} r={6} className={styles.pinDot} />
    </svg>
  );
}

function pointToPair(p: Point2D): string {
  return `${round(p.x)},${round(p.y)}`;
}

/** Two decimals is well below a pixel and keeps the DOM small on an 18-hole course. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}
